import React, { Component } from "react";
import Modal from "react-bootstrap/Modal";

import Backend from "../../../utils/Backend";
import Notify from "../../../utils/Notify";
import Currency from "../../../utils/Currency";

class AddProductToBasketModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      searchTerm: "",
      products: [],
      isLoading: false,
      selectedProduct: null,
      quantity: 1,
      hasSearched: false,
    };

    this.searchTimeout = null;
  }

  componentDidUpdate(prevProps) {
    // Reset state when modal is opened
    if (this.props.show && !prevProps.show) {
      this.setState({
        searchTerm: "",
        products: [],
        selectedProduct: null,
        quantity: 1,
        hasSearched: false,
      });
    }
  }

  _handleSearchChange = (e) => {
    const searchTerm = e.target.value;
    this.setState({ searchTerm });

    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // Debounce search
    if (searchTerm.length >= 2) {
      this.searchTimeout = setTimeout(() => {
        this._searchProducts(searchTerm);
      }, 300);
    } else {
      this.setState({ products: [], hasSearched: false });
    }
  };

  _searchProducts = (searchTerm) => {
    this.setState({ isLoading: true });

    // Use existing Backend method to search products
    Backend.getProductsSearch(searchTerm, 1, 20)
      .then((response) => {
        const products = response.results || response || [];
        this.setState({ 
          products, 
          isLoading: false, 
          hasSearched: true 
        });
      })
      .catch((error) => {
        console.error("Search error:", error);
        this.setState({ 
          products: [], 
          isLoading: false, 
          hasSearched: true 
        });
      });
  };

  _selectProduct = (product) => {
    this.setState({ selectedProduct: product, quantity: 1 });
  };

  _clearSelection = () => {
    this.setState({ selectedProduct: null, quantity: 1 });
  };

  _handleQuantityChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (value > 0) {
      this.setState({ quantity: value });
    }
  };

  _incrementQuantity = () => {
    this.setState((prevState) => ({
      quantity: prevState.quantity + 1,
    }));
  };

  _decrementQuantity = () => {
    this.setState((prevState) => ({
      quantity: Math.max(1, prevState.quantity - 1),
    }));
  };

  _addToBasket = () => {
    const { selectedProduct, quantity } = this.state;
    const { onAdd, existingProducts } = this.props;

    if (!selectedProduct) {
      Notify.error("Please select a product");
      return;
    }

    // Check if product already exists in basket
    const exists = existingProducts?.some(
      (item) => (item.product?.id || item.product_id) === selectedProduct.id
    );

    if (exists) {
      Notify.error("This product is already in the basket");
      return;
    }

    if (onAdd) {
      onAdd({
        product: selectedProduct,
        product_id: selectedProduct.id,
        quantity: quantity,
        price: selectedProduct.price,
      });
    }

    // Reset and close
    this.setState({
      searchTerm: "",
      products: [],
      selectedProduct: null,
      quantity: 1,
      hasSearched: false,
    });

    this.props.onHide();
  };

  _renderProductsList() {
    const { products, isLoading, hasSearched, searchTerm } = this.state;

    if (isLoading) {
      return (
        <div className="text-center py-5">
          <div className="spinner spinner-primary"></div>
          <p className="mt-3 text-muted">Searching products...</p>
        </div>
      );
    }

    if (!hasSearched) {
      return (
        <div className="text-center py-5">
          <i className="fa fa-search fa-3x text-muted mb-3"></i>
          <p className="text-muted">
            Type at least 2 characters to search for products
          </p>
        </div>
      );
    }

    if (products.length === 0) {
      return (
        <div className="text-center py-5">
          <i className="fa fa-inbox fa-3x text-muted mb-3"></i>
          <p className="text-muted">
            No products found for "{searchTerm}"
          </p>
        </div>
      );
    }

    const { existingProducts } = this.props;

    return (
      <div className="product-list" style={{ maxHeight: 300, overflowY: "auto" }}>
        {products.map((product) => {
          const isAlreadyAdded = existingProducts?.some(
            (item) => (item.product?.id || item.product_id) === product.id
          );

          return (
            <div
              key={product.id}
              className={`d-flex align-items-center p-3 border-bottom ${
                isAlreadyAdded ? "bg-light-secondary" : "cursor-pointer hover-bg-light"
              }`}
              onClick={() => !isAlreadyAdded && this._selectProduct(product)}
              style={{ cursor: isAlreadyAdded ? "not-allowed" : "pointer" }}
            >
              {product.image?.thumbnail && (
                <div className="symbol symbol-50 symbol-light mr-3">
                  <img
                    src={product.image.thumbnail}
                    alt={product.title}
                    className="symbol-label"
                    style={{ objectFit: "cover" }}
                  />
                </div>
              )}
              <div className="flex-grow-1">
                <span className="text-dark-75 font-weight-bold d-block">
                  {product.title || product.name}
                </span>
                {product.description && (
                  <span className="text-muted font-size-sm">
                    {product.description.substring(0, 50)}...
                  </span>
                )}
              </div>
              <div className="text-right">
                <span className="text-primary font-weight-bold">
                  {Currency.format(product.price)}
                </span>
                {isAlreadyAdded && (
                  <span className="d-block text-muted font-size-sm">
                    Already added
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  _renderSelectedProduct() {
    const { selectedProduct, quantity } = this.state;

    if (!selectedProduct) return null;

    const subtotal = (selectedProduct.price || 0) * quantity;

    return (
      <div className="bg-light-primary rounded p-4 mb-4">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <h6 className="font-weight-bold mb-0">Selected Product</h6>
          <button
            type="button"
            className="btn btn-icon btn-sm btn-light"
            onClick={this._clearSelection}
          >
            <i className="fa fa-times"></i>
          </button>
        </div>
        
        <div className="d-flex align-items-center mb-3">
          {selectedProduct.image?.thumbnail && (
            <div className="symbol symbol-60 symbol-light mr-3">
              <img
                src={selectedProduct.image.thumbnail}
                alt={selectedProduct.title}
                className="symbol-label"
                style={{ objectFit: "cover" }}
              />
            </div>
          )}
          <div>
            <span className="text-dark font-weight-bold d-block">
              {selectedProduct.title || selectedProduct.name}
            </span>
            <span className="text-primary">
              {Currency.format(selectedProduct.price)} per unit
            </span>
          </div>
        </div>

        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <label className="mb-0 mr-3">Quantity:</label>
            <div className="input-group" style={{ width: 130 }}>
              <div className="input-group-prepend">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={this._decrementQuantity}
                  disabled={quantity <= 1}
                >
                  <i className="fa fa-minus"></i>
                </button>
              </div>
              <input
                type="number"
                className="form-control text-center"
                value={quantity}
                onChange={this._handleQuantityChange}
                min={1}
              />
              <div className="input-group-append">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={this._incrementQuantity}
                >
                  <i className="fa fa-plus"></i>
                </button>
              </div>
            </div>
          </div>
          <div className="text-right">
            <span className="text-muted d-block font-size-sm">Subtotal</span>
            <span className="font-weight-bold text-dark">
              {Currency.format(subtotal)}
            </span>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { show, onHide } = this.props;
    const { searchTerm, selectedProduct } = this.state;

    return (
      <Modal
        show={show}
        onHide={onHide}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fa fa-plus-circle text-primary mr-2"></i>
            Add Product to Basket
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Search Input */}
          <div className="form-group mb-4">
            <label className="font-weight-bold">Search Products</label>
            <div className="input-group">
              <div className="input-group-prepend">
                <span className="input-group-text">
                  <i className="fa fa-search"></i>
                </span>
              </div>
              <input
                type="text"
                className="form-control"
                placeholder="Search by product name..."
                value={searchTerm}
                onChange={this._handleSearchChange}
                autoFocus
              />
            </div>
          </div>

          {/* Selected Product */}
          {this._renderSelectedProduct()}

          {/* Products List */}
          {!selectedProduct && (
            <div className="border rounded">
              {this._renderProductsList()}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light"
            onClick={onHide}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={this._addToBasket}
            disabled={!selectedProduct}
          >
            <i className="fa fa-plus mr-2"></i>
            Add to Basket
          </button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default AddProductToBasketModal;
